import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
    ConfirmationPopupService,
    CustomFormValidators,
    MultiSubscriptionComponent,
} from '@ddv/common-components';
import { UserEntitlements } from '@ddv/entitlements';
import { QueryParamsService } from '@ddv/filters';
import { ManagerService } from '@ddv/layout';
import {
    PERMISSION,
    DashboardModel,
    DashboardPreference,
    getGlobalSaveDialogOptions,
} from '@ddv/models';
import { map, Observable, of } from 'rxjs';

import { DashboardDetails } from '../../models/dashboard-details';
import { DashboardForm } from '../../models/dashboard-form';
import { isShared, Sharing } from '../../models/sharing';
import { DashboardSharingService } from '../../services/dashboard-sharing.service';
import { DashboardService } from '../../services/dashboard.service';

// Only launched dynamically, so you'll see no reference to the selector
// Only launched by DashboardNavComponent
@Component({
    selector: 'app-save-form',
    templateUrl: './save-dashboard-form.component.html',
    styleUrls: ['./save-dashboard-form.component.scss'],
    standalone: false,
})
export class SaveDashboardFormComponent extends MultiSubscriptionComponent implements OnInit, AfterViewChecked {
    @Input() clientCode: string | undefined;
    @Input() selectedOption: 'new' | 'edit' = 'new';
    @Input() isReadOnly = false;
    @Input() dashboardId: number | undefined;
    // This stupidity doesn't have the id on the model, but the model can come like a billion places
    @Input() dashboard: DashboardDetails | DashboardModel | undefined;
    @Input() dashboardGroupName: string | undefined;
    @Input() isDashboardCopy = false;
    @Input() isGlobalEditPartial = false;
    @Input() isInternal = false;
    @Input() entitlements: UserEntitlements | undefined;

    @Output() formSubmitted = new EventEmitter<{ details: DashboardDetails, sharing: Sharing }>();
    @Output() closeForm = new EventEmitter<Event>();

    private readonly cdr = inject(ChangeDetectorRef);
    private readonly confirmationService = inject(ConfirmationPopupService);
    private readonly dashboardService = inject(DashboardService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly managerService = inject(ManagerService);
    private readonly queryParamsService = inject(QueryParamsService);
    private readonly sharingService = inject(DashboardSharingService);

    dashboardForm: UntypedFormGroup | undefined;
    dashboardFormErrors = {
        name: '',
        family: '',
    };
    sharing: Sharing = { toUsers: [], toTeams: [] };
    // This is public because it is updated by the DashboardNavComponent after save
    isSaveDashboardInvoked = false;

    protected isGlobal = false;
    protected saveButtonText = 'Save';

    private privilege: string | undefined;
    private isNameAvailable = true;
    private queryParam: DashboardPreference = {};

    ngOnInit(): void {
        this.subscribeTo(this.queryParamsService.dashboardQueryParams, (dashboardPref: DashboardPreference) => {
            this.queryParam = { ...dashboardPref };
        });

        this.buildDashboardFormModel()
            .subscribe({
                next: (data: DashboardForm) => {
                    this.buildDashboardForm(data);
                    this.cdr.detectChanges();
                },
            });
    }

    ngAfterViewChecked(): void {
        this.cdr.detectChanges();
    }

    onSaveDashboard(formData: DashboardForm): void {
        if (this.isGlobal && !this.isDashboardCopy && !this.isGlobalEditPartial) {
            const confirmDialogOptions = getGlobalSaveDialogOptions();
            this.confirmationService.showConfirmationPopup(confirmDialogOptions).subscribe({
                next: (action) => {
                    if (action === 'confirm') {
                        this.onDashboardSave(formData);
                    }
                },
            });
        } else {
            this.onDashboardSave(formData);
        }
    }

    nameUnavailable(): void {
        this.isSaveDashboardInvoked = false;
        this.isNameAvailable = false;
        this.dashboardFormErrors.name = 'Name already exists. Please try a different name';
    }

    protected checkNameAvailability(): void {
        const control = this.dashboardForm?.get('name');
        if (!control?.valid) {
            return;
        }

        const dashboardName = typeof control.value === 'string' ? control.value.trim() : '';
        if (!this.skipNameAvailabilityCheck(dashboardName)) {
            this.dashboardService.validateDashboardName(this.clientCode ?? '', dashboardName)
                .subscribe((nameIsAvailable) => {
                    if (!nameIsAvailable) {
                        this.nameUnavailable();
                    }
                });
        }
    }

    protected disableSaveButton(): boolean {
        return !this.dashboardForm?.valid ||
            !this.isNameAvailable ||
            this.isSaveDashboardInvoked ||
            (!this.isReadOnly && this.privilege === PERMISSION.VIEW && this.selectedOption === 'edit') ||
            (this.isReadOnly && !!this.dashboard);
    }

    private onDashboardSave(formData: DashboardForm): void {
        this.emitFormDetails(formData);
    }

    private emitFormDetails(formData: DashboardForm): void {
        const converted = this.convertFormDataToDashboardDetails(formData);
        this.isSaveDashboardInvoked = true;
        this.managerService.selectWidget(false, undefined);

        return this.formSubmitted.emit(converted);
    }

    private convertFormDataToDashboardDetails(formData: DashboardForm): { details: DashboardDetails, sharing: Sharing } {
        const dashboardName = formData.name.trim();

        const details: DashboardDetails = new DashboardDetails(
            {
                abbreviation: formData.abbreviation || dashboardName.slice(0, 4),
                allowInfiniteVScroll: true,
                allowInfiniteHScroll: false,
                widgets: [],
                dashboardPreference: {},
                description: formData.description,
                isGlobal: this.isGlobal,
                name: dashboardName,
            },
            [],
        );

        if (this.selectedOption === 'new') {
            return { details, sharing: formData.source === 'shared' ? this.sharing : { toUsers: [], toTeams: [] } };
        }

        if (!this.dashboard) {
            const workspace = this.managerService.getWorkspace();
            details.widgets = this.managerService.getAppWidgetsState()
                .map((widget) => {
                    widget.maximized = false;
                    return widget;
                });

            details.dashboardPreference = this.queryParam;

            details.version = workspace?.getExtraParameters()?.version;
            details.privilege = workspace?.getExtraParameters()?.privilege ?? 'VIEW';

            if (this.selectedOption === 'edit') {
                details.id = workspace?.id ? Number(workspace.id) : undefined;
            }
        } else {
            details.id = this.dashboard.id;
            details.widgets = this.dashboard.widgets;
            details.dashboardPreference = this.dashboard.dashboardPreference;
            details.version = this.dashboard.version;
            details.privilege = this.dashboard.privilege;
        }

        return { details, sharing: formData.source === 'shared' ? this.sharing : { toUsers: [], toTeams: [] } };
    }

    private buildDashboardFormModel(): Observable<DashboardForm> {
        const dashboardFormModel: DashboardForm = {
            name: '',
            description: '',
            source: 'private',
            isDefault: false,
            abbreviation: '',
            family: '',
        };

        if (this.selectedOption === 'new') {
            return of(dashboardFormModel);
        }

        const dashboard = this.dashboard ?? this.managerService.getWorkspace()?.getExtraParameters();
        return this.sharingService.getSharing(this.clientCode!, this.dashboardId)
            .pipe(
                map((sharing) => {
                    this.isGlobal = !!dashboard?.isGlobal;

                    dashboardFormModel.name = dashboard?.name ?? '';
                    if (isShared(sharing)) {
                        this.sharing = sharing;
                    }
                    dashboardFormModel.source = isShared(sharing) ? 'shared' : dashboardFormModel.source;

                    this.privilege = dashboard?.privilege;

                    dashboardFormModel.description = dashboard?.description ?? dashboardFormModel.description;
                    dashboardFormModel.abbreviation = dashboard?.abbreviation ?? dashboardFormModel.abbreviation;
                    dashboardFormModel.family = this.dashboardGroupName ?? 'None';

                    return dashboardFormModel;
                }),
            );
    }

    private buildDashboardForm(details: DashboardForm): void {
        this.dashboardForm = this.formBuilder.group({
            name: [
                details.name,
                [
                    Validators.compose([
                        Validators.required,
                        CustomFormValidators.isInputSpaces,
                    ]),
                    Validators.pattern(/^[\w/\s\-%&^#@()<>]+$/),
                ],
            ],
            description: [details.description],
            source: [details.source],
            isDefault: [details.isDefault],
            abbreviation: [details.abbreviation],
            family: [details.family],
        });

        this.dashboardForm.get('name')?.valueChanges.subscribe(() => this.validateName('name'));
        this.dashboardForm.get('family')?.valueChanges.subscribe(() => this.validateName('family'));
    }

    private validateName(key: 'name' | 'family'): void {
        const control = this.dashboardForm?.get(key);
        this.dashboardFormErrors[key] = '';
        this.isNameAvailable = true;

        if (control && !control.valid) {
            if (control.errors?.required) {
                this.dashboardFormErrors[key] = `Error: ${key} cannot be blank`;
            } else if (control.errors?.isSpaces) {
                this.dashboardFormErrors[key] = `Error: ${key} cannot be only spaces.`;
            } else if (control.errors?.pattern) {
                this.dashboardFormErrors[key] = `Error: ${key} contains an invalid character.`;
            }
        }
    }

    // Don't check if in edit mode and the name is unchanged because I'm allowed to use my own name
    private skipNameAvailabilityCheck(dashboardName: string): boolean {
        return this.selectedOption === 'edit' &&
            dashboardName.toLowerCase() === this.managerService.getWorkspace()?.getExtraParameters()?.name?.toLowerCase() &&
            !this.isDashboardCopy;
    }
}
